<template>
  <div class="flex justify-center items-start mb-3 mt-1">
    <div
      class="relative inline-flex transition-opacity duration-200 motion-reduce:transition-none"
      :style="{ opacity: frameReady ? 1 : 0, visibility: frameReady ? 'visible' : 'hidden' }"
    >
      <div v-if="currentDevice && layout" class="relative">
        <KeyboardFrame :assets-dir="assetsDir" @ready="frameReady = $event" />

        <div
          class="absolute left-0 top-0 w-full h-full flex items-center justify-center"
          :style="{
            paddingTop: layout?.top + 'px',
            paddingLeft: layout?.left + 'px',
            paddingBottom: layout?.bottom + 'px',
            paddingRight: layout?.right + 'px',
          }"
          @contextmenu="handleKeyboardContextmenu"
        >
          <Keyboard
            class="shrink-0"
            :keyboard-config="keyboardConfig!"
            :gap="5"
            :key-class="getKeyClass"
            :key-disabled="mode === 'calibration'"
            :native-event-names="keyboardEventNames"
            @key-event="handleKeyEvent"
          >
            <template #widget>
              <component :is="widgetMap[model]" v-if="model" />
            </template>
            <template #key-label="{ config }">
              <div
                v-if="mode === 'demo'"
                class="relative w-full h-full flex items-center justify-center select-none"
                style="-webkit-text-stroke: unset"
              >
                <span class="whitespace-pre-wrap leading-4">
                  {{ getLocalizedField(config.alias ?? config.name) }}
                </span>
                <span
                  v-if="getKeyRtEnabled(config)"
                  class="absolute top-0 left-0.5 text-[8px] font-[Barlow] leading-2 font-normal text-brand-red"
                  style="-webkit-text-stroke: initial"
                >
                  {{ getKeySensitivityText(config) }}
                </span>
                <span
                  class="absolute bottom-0 text-[8px] font-[Barlow] leading-2 font-normal text-black-white"
                  style="-webkit-text-stroke: initial"
                >
                  {{ getKeyCalibrationText(config) }}
                </span>
              </div>
              <div v-if="mode === 'calibration'">
                <div
                  v-if="config.keywords === 'RT'"
                  class="flex items-center justify-center w-7 h-7"
                >
                  <Icon icon="Hardware-Fill-rt" class="w-7 h-7 text-text-body-default" />
                </div>
                <span v-else class="whitespace-pre-wrap leading-4">
                  {{ getLocalizedField(config.alias ?? config.name) }}
                </span>
              </div>
            </template>
          </Keyboard>
        </div>
      </div>

      <div
        v-if="mode === 'demo' && showKeyboardActions"
        class="absolute top-0 left-full flex flex-col gap-3 pl-4 pt-4"
      >
        <Button color="glass" @click="handleSelectAll">
          {{ t('itinerary.keyboardPanel.selectAll') }}
        </Button>
        <Button color="glass" @click="handleInvertSelection">
          {{ t('itinerary.keyboardPanel.invertSelection') }}
        </Button>
        <Button color="glass" @click="handleSelectWASD">
          {{ t('itinerary.keyboardPanel.selectWASD') }}
        </Button>
        <Button color="glass" @click="handleSelectAZ">
          {{ t('itinerary.keyboardPanel.selectAZ') }}
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, ref, watch } from 'vue'
  import { storeToRefs } from 'pinia'
  import type { HTMLAttributes } from 'vue'
  import { Keyboard, Button, Icon, toast } from '@rapoo/element'
  import { useI18n } from 'vue-i18n'
  import type {
    KeyboardConfig,
    KeyConfig,
    KeyboardKeyEventPayload,
    KeyEventName,
  } from '@rapoo/element'
  import { useDeviceStore } from '@/stores'
  import { KEYCODE_KEYWORDS_MAP, getLocalizedField } from '../../keys/utils/keyMapSettings'
  import widgetMap from '@/components/keyboardWidget'
  import KeyboardFrame from '@/components/KeyboardFrame.vue'
  import { useGlobalContextmenu } from '@/composables/useGlobalContextmenu'
  import { useCalibrationMode } from '../composables/useCalibrationMode'
  import type { ClassicKeyTravel } from '@rapoo/device-sdk'

  const DEFAULT_KEY_TRAVEL: ClassicKeyTravel = {
    absoluteTrigger: 135, // 绝对触发位置 1.35mm
    rtTriggerSensitivity: 50, // RT 快速触发灵敏度 0.50mm
    rtResetSensitivity: 50, // RT 快速触发重置灵敏度 0.50mm
    rtTriggerEnable: false, // RT 快速触发开关
    absoluteTriggerEnable: true, // 绝对触发 开关
    bottomDeadZone: 0, // 单键底部死区 0.00mm
  }

  const WASD_KEYCODES = new Set(['W', 'A', 'S', 'D'])
  const AZ_REGEX = /^[A-Z]$/
  const TOAST_ID = 'itinerary-keyboard'

  const keyboardEventNames: KeyEventName[] = ['mousedown', 'mouseenter', 'click']

  const props = defineProps<{
    mode: 'calibration' | 'demo'
    showKeyboardActions: boolean
    keySelectable: boolean
    keyTravelMap: Map<number, ClassicKeyTravel>
  }>()

  const emit = defineEmits<{
    (e: 'selection-change', keys: { index: number }[]): void
  }>()
  const { t } = useI18n()

  // 按键校准
  const { calibratedKeys } = useCalibrationMode()

  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const layout = computed(() => currentDevice.value?.manifest.layout)
  const model = computed(() => currentDevice.value?.manifest.model)
  const assetsDir = computed(() => currentDevice.value?.manifest?.ui?.assetsDir || '')
  const frameReady = ref(false)

  const keyboardConfig = computed<KeyboardConfig | null>(() => {
    if (!layout.value) return null

    return {
      ...layout.value,
      keys: layout.value.keys.map((row) =>
        row.map((key) => {
          const { w, h, x, y, keywords, ...rest } = key
          const config: KeyConfig = KEYCODE_KEYWORDS_MAP.get(keywords)!

          return {
            ...rest,
            ...config,
            rect: {
              w,
              h,
              x,
              y,
            },
          }
        }),
      ),
    }
  })

  // keywords -> 键位索引映射（index 来源于布局的 key 字段，与设备端一致）
  const keyInfoMap = computed(() => {
    if (!layout.value) return new Map<string, { index: number }>()

    const map = new Map<string, { index: number }>()
    layout.value.keys.forEach((row) => {
      row.forEach((key) => {
        map.set(key.keywords, { index: key.key })
      })
    })
    return map
  })

  // 扁平化所有键位
  const allKeys = computed(() => {
    if (!layout.value) return [] as KeyConfig[]

    return layout.value.keys.flatMap((row) =>
      row.map((key) => {
        const { w, h, x, y, ...rest } = key
        return {
          ...rest,
          rect: { w, h, x, y },
        } as KeyConfig
      }),
    )
  })

  const getKeyTravel = (index: number): ClassicKeyTravel => {
    return props.keyTravelMap.get(index) ?? { ...DEFAULT_KEY_TRAVEL }
  }

  const getKeyInfo = (key: KeyConfig) => {
    return keyInfoMap.value.get(key.keywords)
  }

  const getKeyRtEnabled = (key: KeyConfig) => {
    const info = getKeyInfo(key)
    if (!info) return false
    return getKeyTravel(info.index).rtTriggerEnable
  }

  const getKeySensitivityText = (key: KeyConfig) => {
    const info = getKeyInfo(key)
    if (!info) return ''
    const travel = getKeyTravel(info.index)
    const trigger = (travel.rtTriggerSensitivity / 100).toFixed(2)
    const reset = (travel.rtResetSensitivity / 100).toFixed(2)
    return `${trigger}  ${reset}`
  }

  const getKeyCalibrationText = (key: KeyConfig) => {
    const info = getKeyInfo(key)
    if (!info) return ''
    const travel = getKeyTravel(info.index)
    return (travel.absoluteTrigger / 100).toFixed(2)
  }

  // ---- 选中状态 ----
  const currentSelection = ref<KeyConfig[]>([])
  const isDraggingSelection = ref(false)
  const dragStartKey = ref<KeyConfig | null>(null)
  const dragVisitedKeys = ref(new Set<string>())
  const dragMoved = ref(false)
  const suppressedClickKeyCode = ref<string | null>(null)
  const dragLastKeyCode = ref<string | null>(null)

  const shouldListenGlobalContextmenu = computed(() => Boolean(currentSelection.value.length))

  const cancelSelectedKeyByGlobalContextmenu = () => {
    if (!currentSelection.value.length) return
    currentSelection.value = []
    emitSelectionChange()
    toast.dismiss(TOAST_ID)
  }

  useGlobalContextmenu({
    enabled: shouldListenGlobalContextmenu,
    onContextmenu: cancelSelectedKeyByGlobalContextmenu,
  })

  const hasSelected = (keywords: string) => {
    return currentSelection.value.some((item) => item.keywords === keywords)
  }

  const emitSelectionChange = () => {
    const keys = currentSelection.value
      .map((key) => getKeyInfo(key))
      .filter((item): item is { index: number } => Boolean(item))
    emit('selection-change', keys)
  }

  const setKeySelected = (key: KeyConfig, selected: boolean) => {
    if (selected) {
      if (hasSelected(key.keywords)) return
      currentSelection.value = [...currentSelection.value, key]
      emitSelectionChange()
      return
    }

    currentSelection.value = currentSelection.value.filter((item) => item.keywords !== key.keywords)
    emitSelectionChange()
  }

  const toggleKeySelected = (key: KeyConfig) => {
    setKeySelected(key, !hasSelected(key.keywords))
  }

  const resetDragState = () => {
    isDraggingSelection.value = false
    dragStartKey.value = null
    dragVisitedKeys.value = new Set()
    dragMoved.value = false
    dragLastKeyCode.value = null
    window.removeEventListener('mouseup', handleGlobalMouseUp)
  }

  const applyDragSelection = (key: KeyConfig) => {
    if (dragVisitedKeys.value.has(key.keywords)) return

    dragVisitedKeys.value.add(key.keywords)
    dragLastKeyCode.value = key.keywords
    // 拖动经过时切换：未选中→选中，已选中→取消选中
    toggleKeySelected(key)
  }

  /** 全局 mouseup：拖出键盘区域松开也能正确结束拖选 */
  const handleGlobalMouseUp = () => {
    if (!isDraggingSelection.value) return
    suppressedClickKeyCode.value = dragMoved.value ? dragLastKeyCode.value : null
    resetDragState()
  }

  const startDragSelection = (key: KeyConfig) => {
    isDraggingSelection.value = true
    dragStartKey.value = key
    dragVisitedKeys.value = new Set()
    dragMoved.value = false
    dragLastKeyCode.value = key.keywords
    suppressedClickKeyCode.value = null
    window.addEventListener('mouseup', handleGlobalMouseUp)
  }

  const showSelectionToast = () => {
    toast(t('itinerary.keyboardPanel.selectionToast'), {
      id: TOAST_ID,
      duration: Infinity,
      class: 'text-state-success',
    })
  }

  const dismissSelectionToast = () => {
    toast.dismiss(TOAST_ID)
  }

  const clearAllSelectedKeys = () => {
    if (!currentSelection.value.length) return

    currentSelection.value = []
    emitSelectionChange()
    resetDragState()
    suppressedClickKeyCode.value = null
  }

  const handleKeyboardContextmenu = (e: MouseEvent) => {
    if (!currentSelection.value.length) return
    e.preventDefault()
    clearAllSelectedKeys()
    dismissSelectionToast()
  }

  // ---- 按钮操作 ----
  const handleSelectAll = () => {
    currentSelection.value = [...allKeys.value]
    emitSelectionChange()
  }

  const handleInvertSelection = () => {
    const selectedKeyCodes = new Set(currentSelection.value.map((item) => item.keywords))
    currentSelection.value = allKeys.value.filter((key) => !selectedKeyCodes.has(key.keywords))
    emitSelectionChange()
  }

  const handleSelectWASD = () => {
    currentSelection.value = allKeys.value.filter((key) => WASD_KEYCODES.has(key.keywords))
    emitSelectionChange()
  }

  const handleSelectAZ = () => {
    currentSelection.value = allKeys.value.filter((key) => AZ_REGEX.test(key.keywords))
    emitSelectionChange()
  }

  // ---- 键盘事件 ----
  const handleKeyEvent = (ctx: KeyboardKeyEventPayload) => {
    if (!props.keySelectable) return

    const { eventName, data, nativeEvent } = ctx

    if (eventName === 'mousedown') {
      nativeEvent.preventDefault()
      startDragSelection(data)
      return
    }

    if (eventName === 'mouseenter') {
      if (!isDraggingSelection.value) return
      if (dragStartKey.value?.keywords === data.keywords) return

      if (!dragMoved.value && dragStartKey.value) {
        applyDragSelection(dragStartKey.value)
      }

      dragMoved.value = true
      applyDragSelection(data)
      return
    }

    if (eventName === 'click') {
      if (suppressedClickKeyCode.value === data.keywords) {
        suppressedClickKeyCode.value = null
        return
      }

      toggleKeySelected(data)
    }
  }

  // ---- 样式 ----
  const calibratedKeyCodeSet = computed(() => {
    if (!layout.value) return new Set<string>()

    // index -> keywords 映射
    const indexToKeywords = new Map<number, string>()
    layout.value.keys.forEach((row) => {
      row.forEach((key) => {
        indexToKeywords.set(key.key, key.keywords)
      })
    })

    const set = new Set<string>()
    for (const { index } of calibratedKeys.value) {
      const keywords = indexToKeywords.get(index)
      if (keywords) set.add(keywords)
    }
    return set
  })

  const getKeyClass = (key: KeyConfig): HTMLAttributes['class'] => {
    const isActive = hasSelected(key.keywords)
    const calibrated = calibratedKeyCodeSet.value.has(key.keywords)

    const base = 'transition-all text-black-white hover:text-text-glass-hover'

    if (calibrated) {
      return `${base} bg-green-active disabled:text-text-important-default`
    }

    if (isActive) {
      return `${base} border-brand-blue1 bg-white text-black hover:text-black`
    }

    return `${base} bg-key`
  }

  // ---- 选中提示 & 清理 ----
  // 预设模式切换时清空选中
  watch(
    () => props.keySelectable,
    (selectable) => {
      if (!selectable) {
        clearAllSelectedKeys()
        dismissSelectionToast()
      }
    },
  )

  onBeforeUnmount(() => {
    window.removeEventListener('mouseup', handleGlobalMouseUp)
    dismissSelectionToast()
  })

  // 监听选中数量，自动控制提示显示
  watch(
    () => currentSelection.value.length,
    (count) => {
      if (count > 0) {
        showSelectionToast()
        return
      }
      dismissSelectionToast()
    },
    { immediate: true },
  )
</script>

<style scoped></style>
